import * as React from "react";
import { useConfigState } from "../../../context/settings/config";
import { RuleSetting } from "../../../context/settings/types";
import {
  calcSelectAllConfig,
  calcRemoveAllConfig,
} from "../utils/calcActiveConfig";
import { useActions } from "../../../context/settings/config/useActions";
import { ActiveRules } from "../../../context/settings/config/types";

export function useRuleSettings() {
  const configState = useConfigState();
  const { updateConfigRules, updateRuleConfig, bulkUpdateRuleConfig } =
    useActions();
  const [propagated, setPropagated] = React.useState<ActiveRules>({});

  const handleCheck = React.useCallback(function handleCheck(
    rule: RuleSetting
  ) {
    return () => {
      void updateConfigRules(rule);
    };
  },
  []);

  const updatePriority = React.useCallback(function updatePriority(updated: RuleSetting) {
    void updateRuleConfig(updated);
  }, []);

  const resetRules = React.useCallback(
    function resetRules() {
      setPropagated({ ...configState.saved });
    },
    [configState.shouldReset]
  );

  const updateActive = React.useCallback(
    function updateActive() {
      setTimeout(() => {
        setPropagated(configState.active);
      }, 300);
    },
    [configState.active]
  );

  const clearAll = React.useCallback(
    function clearAll(visible: RuleSetting[]) {
      return () => {
        const active = calcRemoveAllConfig(visible, configState);
        setPropagated(active);
        bulkUpdateRuleConfig(active);
      };
    },
    [configState.active]
  );

  const selectAll = React.useCallback(
    function selectAll(visible: RuleSetting[]) {
      return () => {
        const active = calcSelectAllConfig(visible, configState);
        setPropagated(active);
        bulkUpdateRuleConfig(active);
      };
    },
    [configState.active]
  );

  React.useEffect(() => {
    setPropagated({ ...configState.saved });
  }, [configState.saved]);

  React.useEffect(() => {
    if (configState.shouldReset) {
      setPropagated({ ...configState.saved });
    }
  }, [configState.shouldReset]);

  return React.useMemo(
    () => ({
      checked: configState.active,
      propagated,
      clearAll,
      selectAll,
      updateActive,
      handleCheck,
      resetRules,
      updatePriority,
      insightType: configState.insightType,
    }),
    [configState.active, propagated, configState.insightType]
  );
}
